/**
 * Global-search filter bar: the pure half (需求 FR-14 §5.3.2 ③).
 *
 * Selection, ordering and the date presets live here so the face is markup
 * only, the same split `queryCommands` / `run-search` already follow. Nothing
 * here reads a filter's *meaning* — a preset is a range of milliseconds, a chip
 * summary counts ids; which of a module's sections honour either is decided on
 * the far side of `executeCommand`.
 */
import type { ResolvedSearchFilter, SearchFilterValue } from '@hibro/shared';

import type { SearchTab } from './search-contract';

/**
 * The filters to draw for `tab`, in display order.
 *
 * Empty for the All tab, and that is the rule rather than an accident of the
 * data: a filter is one module's vocabulary, and the All tab spans every
 * module, so there is no owner to attribute "from" to. It is also what keeps
 * `SearchQuery.filters` unambiguous — only the declaring module's own providers
 * ever run while a value is set.
 */
export function filtersForTab(
  filters: readonly ResolvedSearchFilter[],
  tab: SearchTab,
): ResolvedSearchFilter[] {
  if (tab.kind !== 'app') {
    return [];
  }
  return filters
    .filter((filter) => filter.moduleId === tab.moduleId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** Filter values pruned to the ones `tab` can actually show (见 `filtersForTab`). */
export function filterValuesForTab(
  values: Readonly<Record<string, SearchFilterValue>>,
  visible: readonly ResolvedSearchFilter[],
): Record<string, SearchFilterValue> {
  const allowed = new Set(visible.map((filter) => filter.id));
  const kept: Record<string, SearchFilterValue> = {};
  for (const [id, value] of Object.entries(values)) {
    if (allowed.has(id)) {
      kept[id] = value;
    }
  }
  return kept;
}

/** The quick ranges a `dateRange` filter offers before the calendar. */
export const DATE_PRESETS = ['today', 'last7', 'last30'] as const;

export type DatePreset = (typeof DATE_PRESETS)[number];

/**
 * A preset as an inclusive epoch-ms range, in the **local** timezone.
 *
 * Local rather than UTC because "today" is the user's day: bounding it by UTC
 * midnight would, east of Greenwich, silently drop this morning's messages from
 * a filter the user reads as "today".
 */
export function datePresetRange(
  preset: DatePreset,
  now: Date = new Date(),
): { from: number; to: number } {
  const endOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    23,
    59,
    59,
    999,
  );
  const daysBack = preset === 'today' ? 0 : preset === 'last7' ? 6 : 29;
  const start = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - daysBack,
  );
  return { from: start.getTime(), to: endOfToday.getTime() };
}

/** A `dateRange` value from a calendar selection, snapped to whole local days. */
export function dateRangeValue(
  from: Date | undefined,
  to: Date | undefined,
): SearchFilterValue {
  return {
    type: 'dateRange',
    ...(from === undefined ? {} : { from: startOfDay(from).getTime() }),
    // A calendar cell means the whole day. Taking its midnight as the upper
    // bound would exclude everything that happened on the day the user picked.
    ...(to === undefined ? {} : { to: endOfDay(to).getTime() }),
  };
}

/** The `Date` pair a calendar should show for a stored `dateRange`. */
export function dateRangeSelection(value: SearchFilterValue | undefined): {
  from?: Date;
  to?: Date;
} {
  if (value?.type !== 'dateRange') {
    return {};
  }
  return {
    ...(value.from === undefined ? {} : { from: new Date(value.from) }),
    ...(value.to === undefined ? {} : { to: new Date(value.to) }),
  };
}

/** Toggle one candidate in a `picker` value, honouring single vs multi select. */
export function togglePickerId(
  value: SearchFilterValue | undefined,
  id: string,
  multiple: boolean,
): SearchFilterValue | null {
  const current = value?.type === 'picker' ? value.ids : [];
  if (current.includes(id)) {
    const ids = current.filter((entry) => entry !== id);
    // Clearing the last tick clears the filter itself, so "nothing ticked" and
    // "never opened" stay the same state (见 `pruneSearchFilterValues`).
    return ids.length === 0 ? null : { type: 'picker', ids };
  }
  return { type: 'picker', ids: multiple ? [...current, id] : [id] };
}

/**
 * What a chip shows next to its label once set: the number of picked ids, or
 * the two ends of a range. `null` means "unset — draw the bare label".
 *
 * Returns data rather than a string because the pieces need translating and
 * date formatting, which is the face's business.
 */
export type FilterChipSummary =
  | { readonly kind: 'count'; readonly count: number }
  | { readonly kind: 'range'; readonly from?: Date; readonly to?: Date };

export function filterChipSummary(
  value: SearchFilterValue | undefined,
): FilterChipSummary | null {
  if (value === undefined) {
    return null;
  }
  if (value.type === 'picker') {
    return value.ids.length === 0
      ? null
      : { kind: 'count', count: value.ids.length };
  }
  if (value.from === undefined && value.to === undefined) {
    return null;
  }
  return {
    kind: 'range',
    ...(value.from === undefined ? {} : { from: new Date(value.from) }),
    ...(value.to === undefined ? {} : { to: new Date(value.to) }),
  };
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function endOfDay(date: Date): Date {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
    999,
  );
}
